import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, MoreVertical, Plus, RefreshCcw, ShieldOff, Trash2, UserMinus, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function formatRelative(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
        return d.toLocaleDateString();
    } catch { return ""; }
}

function formatExpires(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (d.getTime() - Date.now()) / 1000;
        if (diff <= 0) return "expired";
        if (diff < 86400) return `${Math.round(diff / 3600)}h left`;
        return `${Math.round(diff / 86400)}d left`;
    } catch { return ""; }
}

export default function TeamSection() {
    const { user: currentUser } = useAuth();
    const isOwner = currentUser?.role === "owner";
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [confirm, setConfirm] = useState(null); // { kind, target }

    const load = async () => {
        setLoading(true);
        try {
            const [m, i] = await Promise.all([
                api.get("/team/members"),
                isOwner ? api.get("/team/invites") : Promise.resolve({ data: [] }),
            ]);
            setMembers(m.data || []);
            setInvites(i.data || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err) || "Couldn't load your team");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [isOwner]);

    const remove = (member) => setConfirm({ kind: "remove_member", target: member });
    const resend = async (inviteId) => {
        try {
            await api.post(`/team/invites/${inviteId}/resend`);
            toast.success("Invitation resent — the previous link is no longer valid");
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err) || "Couldn't resend invitation"); }
    };
    const revoke = (invite) => setConfirm({ kind: "revoke_invite", target: invite });
    const promote = (member) => setConfirm({ kind: "promote", target: member });
    const demote = (member) => setConfirm({ kind: "demote", target: member });

    // Owner invariant: never let the last-Owner action be exposed in the UI.
    // Backend enforces this too, but hiding the button avoids a guaranteed 400.
    const ownerCount = members.filter((m) => m.role === "owner").length;

    return (
        <div className="space-y-8" data-testid="team-section">
            {/* Members */}
            <div className="rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <div>
                        <p className="overline mb-1">Team</p>
                        <h2 className="font-display text-2xl tracking-tight text-slate-900">Active members ({members.length})</h2>
                    </div>
                    {isOwner && (
                        <button onClick={() => setInviteOpen(true)}
                            data-testid="team-invite-btn"
                            className="btn-terra text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2">
                            <UserPlus className="w-4 h-4" /> Invite member
                        </button>
                    )}
                </div>
                {loading ? (
                    <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading team…</div>
                ) : (
                    <table className="w-full text-sm" data-testid="team-members-table">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-100 bg-slate-50/50">
                                <th className="px-6 py-3 font-medium">Member</th>
                                <th className="px-6 py-3 font-medium">Role</th>
                                <th className="px-6 py-3 font-medium">Joined</th>
                                <th className="px-6 py-3 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((m) => (
                                <tr key={m.user_id} data-testid={`team-member-row-${m.user_id}`} className="border-b border-slate-50 last:border-b-0">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 shrink-0">
                                                {(m.name || m.email).split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-slate-900 truncate">{m.name || m.email}</div>
                                                <div className="text-xs text-slate-500 truncate">{m.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`text-xs px-2 py-1 rounded-full ${m.role === "owner" ? "bg-[#C55A46]/10 text-[#8B3A2A]" : "bg-slate-100 text-slate-600"}`}>
                                            {m.role === "owner" ? "Owner" : "Member"}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-600 text-xs">{formatRelative(m.created_at)}</td>
                                    <td className="px-6 py-4 text-right">
                                        {isOwner && m.user_id !== currentUser?.user_id && m.role !== "owner" && (
                                            <>
                                                <button onClick={() => promote(m)}
                                                    data-testid={`team-promote-member-${m.user_id}`}
                                                    title="Promote to Owner"
                                                    className="text-slate-400 hover:text-[#C55A46] p-1 mr-2 rounded transition-colors">
                                                    <UserPlus className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => remove(m)}
                                                    data-testid={`team-remove-member-${m.user_id}`}
                                                    title="Remove member"
                                                    className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors">
                                                    <UserMinus className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                        {isOwner && m.user_id !== currentUser?.user_id && m.role === "owner" && ownerCount > 1 && (
                                            <button onClick={() => demote(m)}
                                                data-testid={`team-demote-owner-${m.user_id}`}
                                                title="Demote to Member"
                                                className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors">
                                                <ShieldOff className="w-4 h-4" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pending invitations — Owner only */}
            {isOwner && (
                <div className="rounded-xl border border-slate-200 bg-white">
                    <div className="p-6 border-b border-slate-100">
                        <p className="overline mb-1">Invitations</p>
                        <h2 className="font-display text-2xl tracking-tight text-slate-900">Pending ({invites.length})</h2>
                    </div>
                    {loading ? null : invites.length === 0 ? (
                        <div className="p-12 text-center text-sm text-slate-500" data-testid="team-invites-empty">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                                <Mail className="w-4 h-4 text-slate-400" />
                            </div>
                            No invitations pending — send one to bring a teammate in.
                        </div>
                    ) : (
                        <table className="w-full text-sm" data-testid="team-invites-table">
                            <thead>
                                <tr className="text-left text-xs text-slate-500 border-b border-slate-100 bg-slate-50/50">
                                    <th className="px-6 py-3 font-medium">Email</th>
                                    <th className="px-6 py-3 font-medium">Role</th>
                                    <th className="px-6 py-3 font-medium">Sent</th>
                                    <th className="px-6 py-3 font-medium">Expires</th>
                                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invites.map((i) => (
                                    <tr key={i.invite_id} data-testid={`team-invite-row-${i.invite_id}`} className="border-b border-slate-50 last:border-b-0">
                                        <td className="px-6 py-4 font-medium text-slate-900">{i.email}</td>
                                        <td className="px-6 py-4 text-slate-600">{i.role === "owner" ? "Owner" : "Member"}</td>
                                        <td className="px-6 py-4 text-slate-500 text-xs">{formatRelative(i.last_resent_at || i.sent_at)}</td>
                                        <td className="px-6 py-4 text-slate-500 text-xs">{formatExpires(i.expires_at)}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => resend(i.invite_id)}
                                                data-testid={`team-resend-${i.invite_id}`}
                                                title="Resend (rotates token, invalidates the previous link)"
                                                className="text-slate-500 hover:text-slate-900 p-1 mr-2 rounded transition-colors">
                                                <RefreshCcw className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => revoke(i)}
                                                data-testid={`team-revoke-${i.invite_id}`}
                                                title="Revoke"
                                                className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {inviteOpen && (
                <InviteMemberDialog onClose={() => setInviteOpen(false)} onSent={load} />
            )}
            {confirm && (
                <TeamConfirmDialog
                    kind={confirm.kind}
                    target={confirm.target}
                    onClose={() => setConfirm(null)}
                    onDone={() => { setConfirm(null); load(); }}
                />
            )}
        </div>
    );
}


function TeamConfirmDialog({ kind, target, onClose, onDone }) {
    const [busy, setBusy] = useState(false);
    // Config keyed by action — keeps semantics identical to the previous
    // native confirm()s but rendered as a professional in-app modal.
    const config = {
        promote: {
            testid: "confirm-promote-dialog",
            title: "Promote to Owner?",
            intro: `You're about to promote ${target.name || target.email} to Owner.`,
            highlight: "Owners have full administrative access to this workspace. They can:",
            bullets: [
                "Manage workspace settings",
                "Invite and remove team members",
                "Promote other members",
                "Access all clients and projects",
            ],
            warning: "This action cannot currently be undone from the ClientSpace interface.",
            cta: "Promote to Owner",
            ctaTestid: "confirm-promote-submit",
            ctaVariant: "primary",
            call: () => api.post(`/team/members/${target.user_id}/promote`),
            success: `${target.name || target.email} is now an Owner`,
            fallbackError: "Couldn't promote member",
        },
        remove_member: {
            testid: "confirm-remove-dialog",
            title: "Remove member?",
            intro: `You're about to remove ${target.name || target.email} from this workspace.`,
            highlight: "Their access will be revoked immediately:",
            bullets: [
                "Any active session is invalidated",
                "They can no longer view clients or portals",
                "Their user record is kept for audit history",
            ],
            warning: "This cannot currently be undone from the ClientSpace interface.",
            cta: "Remove member",
            ctaTestid: "confirm-remove-submit",
            ctaVariant: "danger",
            call: () => api.delete(`/team/members/${target.user_id}`),
            success: "Member removed",
            fallbackError: "Couldn't remove member",
        },
        revoke_invite: {
            testid: "confirm-revoke-dialog",
            title: "Revoke invitation?",
            intro: `You're about to revoke the invitation sent to ${target.email}.`,
            highlight: "The invitation link will stop working immediately:",
            bullets: [
                "The recipient can no longer accept this invite",
                "You can send a fresh invitation to the same email later",
            ],
            warning: null,
            cta: "Revoke invitation",
            ctaTestid: "confirm-revoke-submit",
            ctaVariant: "danger",
            call: () => api.delete(`/team/invites/${target.invite_id}`),
            success: "Invitation revoked",
            fallbackError: "Couldn't revoke invitation",
        },
        demote: {
            testid: "confirm-demote-dialog",
            title: "Demote to Member?",
            intro: `You're about to change ${target.name || target.email} from Owner to Member.`,
            highlight: "Members can continue working with clients, deliverables, files, comments, approvals and project progress, but they cannot manage workspace administration or team permissions.",
            bullets: [
                "Full access to clients, deliverables and files",
                "Can leave comments and approve on behalf of the workspace",
                "Cannot manage workspace settings",
                "Cannot invite, remove, promote or demote team members",
            ],
            warning: "They will lose Owner privileges immediately and will need to sign in again.",
            cta: "Demote to Member",
            ctaTestid: "confirm-demote-submit",
            ctaVariant: "danger",
            call: () => api.post(`/team/members/${target.user_id}/demote`),
            success: `${target.name || target.email} is now a Member`,
            fallbackError: "Couldn't demote Owner",
        },
    }[kind];

    if (!config) return null;

    const submit = async () => {
        if (busy) return; // guard duplicate submissions
        setBusy(true);
        try {
            await config.call();
            toast.success(config.success);
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err) || config.fallbackError);
            setBusy(false); // keep the modal open so the user can retry / cancel
        }
    };

    return (
        <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
            <DialogContent data-testid={config.testid} className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">{config.title}</DialogTitle>
                    <DialogDescription className="text-slate-600">{config.intro}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className={`rounded-lg border p-4 ${config.ctaVariant === "danger" ? "border-rose-200 bg-rose-50/60" : "border-[#C55A46]/20 bg-[#C55A46]/5"}`}>
                        <p className="text-sm font-medium text-slate-800">{config.highlight}</p>
                        <ul className="mt-2 text-sm text-slate-700 space-y-1">
                            {config.bullets.map((b) => (
                                <li key={b} className="flex gap-2"><span className="text-slate-400">•</span>{b}</li>
                            ))}
                        </ul>
                    </div>
                    {config.warning && (
                        <p className="text-xs text-slate-500" data-testid={`${config.testid}-warning`}>
                            {config.warning}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <button type="button" onClick={onClose} disabled={busy}
                        data-testid={`${config.testid}-cancel`}
                        className="text-sm px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="button" onClick={submit} disabled={busy}
                        data-testid={config.ctaTestid}
                        className={`text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2 text-white disabled:opacity-70 ${config.ctaVariant === "danger" ? "bg-rose-600 hover:bg-rose-700" : "bg-[#C55A46] hover:bg-[#B04E3D]"}`}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {config.cta}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function InviteMemberDialog({ onClose, onSent }) {
    const [email, setEmail] = useState("");
    const role = "member"; // V1 policy: invites always create Members. Promote via a separate action.
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        const clean = email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
            toast.error("Enter a valid email address");
            return;
        }
        setBusy(true);
        try {
            await api.post("/team/invites", { email: clean, role });
            toast.success(`Invitation sent to ${clean}`);
            onSent && onSent();
            onClose();
        } catch (err) {
            const detail = formatApiErrorDetail(err);
            toast.error(detail || "Couldn't send invitation");
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent data-testid="invite-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">Invite a teammate</DialogTitle>
                    <DialogDescription>They'll get an email with a secure link that stays valid for 7 days.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label htmlFor="invite-email">Email</Label>
                        <Input id="invite-email" type="email" value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="invite-email-input"
                            className="mt-1 ring-terra"
                            placeholder="teammate@example.com" required />
                    </div>
                    <div>
                        <Label>Role</Label>
                        <div className="mt-2 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50">
                            <span data-testid="invite-role-fixed" className="font-medium capitalize">Member</span>
                            <span className="text-xs text-slate-500 block mt-0.5">
                                Access to all client work, no team management. You can promote them to Owner later.
                            </span>
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={onClose}
                            className="text-sm px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100">
                            Cancel
                        </button>
                        <button type="submit" disabled={busy}
                            data-testid="invite-submit"
                            className="btn-terra text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Send invite
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
